import { useMutation, useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useNotifications } from '@sequencehq/core-components'
import { usePermissions } from 'lib/rbac'

import { SETTINGS_STALE_TIME } from 'SettingsV2/settings.constants'

type LabsSettingsUpdate = {
  labsEnabled: boolean
  mcp: { enabled: boolean }
  emailAgent: { enabled: boolean }
}

export const useLabsSettings = () => {
  const { displayNotification } = useNotifications()
  const { can } = usePermissions()

  const canUpdate = can('org.settings:update')

  const { data, isLoading, isError } = useQuery(
    dashboardv99990101Client.getSequenceLabsSettings,
    undefined,
    { staleTime: SETTINGS_STALE_TIME }
  )

  const mutator = useMutation(dashboardv99990101Client.putSequenceLabsSettings)

  const isLabsEnabled = Boolean(data?.labsEnabled)
  const isMcpEnabled = Boolean(data?.mcp.enabled)
  const mcpServerName = data?.mcp.serverName ?? ''
  const mcpServerUrl = data?.mcp.serverUrl ?? ''

  const isEmailAgentEnabled = Boolean(data?.emailAgent?.enabled)
  const emailAgentAddress = data?.emailAgent?.emailAddress ?? ''

  const update = (
    next: LabsSettingsUpdate,
    successMessage: string,
    errorMessage: string
  ) => {
    mutator.mutate(next, {
      onSuccess: () => displayNotification(successMessage, { type: 'success' }),
      onError: () => displayNotification(errorMessage, { type: 'error' })
    })
  }

  const toggleLabs = (enabled: boolean) =>
    update(
      // Disabling Labs also disables every feature block (server-enforced
      // cascade).
      {
        labsEnabled: enabled,
        mcp: { enabled: enabled && isMcpEnabled },
        emailAgent: { enabled: enabled && isEmailAgentEnabled }
      },
      'Labs settings updated',
      'Failed to update Labs settings'
    )

  const toggleMcp = (enabled: boolean) =>
    update(
      {
        labsEnabled: isLabsEnabled,
        mcp: { enabled },
        emailAgent: { enabled: isEmailAgentEnabled }
      },
      'MCP updated',
      'Failed to update MCP'
    )

  const toggleEmailAgent = (enabled: boolean) =>
    update(
      {
        labsEnabled: isLabsEnabled,
        mcp: { enabled: isMcpEnabled },
        emailAgent: { enabled }
      },
      'AR agent updated',
      'Failed to update AR agent'
    )

  return {
    isLabsEnabled,
    isMcpEnabled,
    mcpServerName,
    mcpServerUrl,
    isEmailAgentEnabled,
    emailAgentAddress,
    isLoading,
    isError,
    isPending: mutator.isPending,
    canUpdate,
    toggleLabs,
    toggleMcp,
    toggleEmailAgent
  }
}
